import {
  calculateTeamScoreFromRoundHistory,
  getGameOutcome,
  isRoundComplete,
} from '../math/spadesMath';
import { TEAM1, TEAM2 } from './constants';
import type { NilSetting, Round } from '../../types';

const SHOWS_KEY = 'shareTipShows';
const FINISHED_GAMES_KEY = 'shareTipFinishedGames';
const E2E_OPT_IN_KEY = 'shareTipInE2E';
const FIREBASE_SIGN_IN_DB = 'firebaseLocalStorageDb';
const FIREBASE_SIGN_IN_KEY = 'firebase:authUser:';

/**
 * After the last bid of a hand, when the first cards go down. Timed from that
 * rather than the last hand's made: Clarity showed 2:48 to 13:19 between the
 * two, so no fixed wait could clear the deal. The hands themselves ran about
 * 5 to 11 minutes, so this is 3 or 4 tricks in, 2 in the slowest.
 */
export const SHARE_TIP_DELAY_MS = 90_000;

/** The first showing, then one reminder. */
const MAX_SHOWS = 2;
/** The game the first showing appeared in, then 3 more. */
const FINISHED_GAMES_BEFORE_REMINDER = 4;
/** A game with no score limit never gets a winner, so this many hands is what
 *  makes it a finished game once the next one starts. */
const NO_LIMIT_FINISHED_HANDS = 5;

function readCount(key: string): number {
  const count = Number(localStorage.getItem(key));
  return Number.isInteger(count) && count >= 0 ? count : 0;
}

function writeCount(key: string, count: number) {
  try {
    localStorage.setItem(key, String(count));
  } catch {
    // Without storage canOfferShareTip stays false, so nothing repeats.
  }
}

/**
 * Whether the share popup may appear on this device now. End-to-end runs play
 * hands and tap what's underneath, so they only get the popup when a spec asks
 * for it.
 */
export function canOfferShareTip(): boolean {
  try {
    if (
      import.meta.env.VITE_E2E_TEST === 'true' &&
      localStorage.getItem(E2E_OPT_IN_KEY) === null
    ) {
      return false;
    }
    const shows = readCount(SHOWS_KEY);
    if (shows === 0) return true;
    return (
      shows < MAX_SHOWS &&
      readCount(FINISHED_GAMES_KEY) >= FINISHED_GAMES_BEFORE_REMINDER
    );
  } catch {
    return false;
  }
}

/**
 * Whether this device started a share before the popup existed to record it.
 * Sharing signs the device in to Firebase and watching doesn't, and Firebase
 * keeps that sign-in in IndexedDB, or localStorage where it can't
 * (tests/spades-watch.spec.ts fails first if that changes).
 */
export async function hasSharedBefore(): Promise<boolean> {
  try {
    if (
      Object.keys(localStorage).some((key) =>
        key.startsWith(FIREBASE_SIGN_IN_KEY),
      )
    ) {
      return true;
    }
    const databases = await indexedDB.databases();
    return databases.some((database) => database.name === FIREBASE_SIGN_IN_DB);
  } catch {
    return false;
  }
}

/** Returns which showing this is, for the copy and analytics. */
export function recordShareTipShown(): 1 | 2 {
  try {
    const showing = readCount(SHOWS_KEY) + 1;
    writeCount(SHOWS_KEY, showing);
    writeCount(FINISHED_GAMES_KEY, 0);
    return showing > 1 ? 2 : 1;
  } catch {
    return 1;
  }
}

/** The player has used the feature: no popup, and no reminder, ever again. */
export function retireShareTip() {
  writeCount(SHOWS_KEY, MAX_SHOWS);
}

interface EndedGame {
  roundHistory: Round[];
  currentRound: Round;
  scoreLimit: number | null;
  nilScoringRule: NilSetting | null;
}

function isGameFinished({
  roundHistory,
  currentRound,
  scoreLimit,
  nilScoringRule,
}: EndedGame) {
  const played = isRoundComplete(currentRound)
    ? [...roundHistory, currentRound]
    : roundHistory;
  if (scoreLimit == null) return played.length >= NO_LIMIT_FINISHED_HANDS;
  const score = (team: typeof TEAM1 | typeof TEAM2) =>
    calculateTeamScoreFromRoundHistory(played, team, nilScoringRule).teamScore;
  const { winner, tieAt } = getGameOutcome(
    score(TEAM1),
    score(TEAM2),
    scoreLimit,
  );
  return winner != null || tieAt != null;
}

/** Called as a new game replaces this one, the one moment every game passes
 *  through exactly once, however it ended. */
export function countFinishedGame(game: EndedGame) {
  try {
    if (!isGameFinished(game)) return;
    writeCount(FINISHED_GAMES_KEY, readCount(FINISHED_GAMES_KEY) + 1);
  } catch {
    // Same as writeCount.
  }
}
